import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreateSampleRulePayload, SampleRuleConditions, UpdateSampleRulePayload } from "@unbox/api";
import { handleError } from "@/api/error";
import { invalidateRulesDashboard } from "@/api/rules";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

// ── Query key builders ────────────────────────────────────────────────────────

const sampleRulesKey = (merchantId: string, workspaceId: string | null | undefined) =>
  ["sample-rules", merchantId, workspaceId] as const;

const sampleRulesCountKey = (merchantId: string, workspaceId: string | null | undefined) =>
  ["sample-rules-count", merchantId, workspaceId] as const;

const sampleExemptionsKey = (merchantId: string, workspaceId: string | null | undefined) =>
  ["sample-rule-exemptions", merchantId, workspaceId] as const;

const sampleRulePreviewKey = (
  merchantId: string,
  workspaceId: string | null | undefined,
  conditions: SampleRuleConditions | null,
) => ["sample-rule-preview", merchantId, workspaceId, conditions] as const;

// ── Invalidation ───────────────────────────────────────────────────────────────

const invalidateSampleRules = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ["sample-rules"] });
  queryClient.invalidateQueries({ queryKey: ["sample-rules-count"] });
  invalidateRulesDashboard(queryClient);
};

// ── Rules CRUD ─────────────────────────────────────────────────────────────────

export const useSampleRules = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: sampleRulesKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.rules.$get({ param: scope.param })
        : await client.merchant[":merchantId"].samples.rules.$get({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useSampleRulesCount = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: sampleRulesCountKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.rules.count.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].samples.rules.count.$get({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useCreateSampleRule = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateSampleRulePayload) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.rules.$post({
            param: scope.param,
            json: payload,
          })
        : await client.merchant[":merchantId"].samples.rules.$post({
            param: scope.param,
            json: payload,
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateSampleRules(queryClient),
  });
};

export const useUpdateSampleRule = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ruleId, ...patch }: { ruleId: string } & UpdateSampleRulePayload) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.rules[":ruleId"].$patch({
            param: { ...scope.param, ruleId },
            json: patch,
          })
        : await client.merchant[":merchantId"].samples.rules[":ruleId"].$patch({
            param: { ...scope.param, ruleId },
            json: patch,
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateSampleRules(queryClient),
  });
};

export const useDeleteSampleRule = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.rules[":ruleId"].$delete({
            param: { ...scope.param, ruleId },
          })
        : await client.merchant[":merchantId"].samples.rules[":ruleId"].$delete({
            param: { ...scope.param, ruleId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateSampleRules(queryClient),
  });
};

// ── Impact preview (draft, not persisted) ───────────────────────────────────────

export const useSampleRulePreview = (conditions: SampleRuleConditions | null) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: sampleRulePreviewKey(scope.merchantId, scope.workspaceId, conditions),
    queryFn: async () => {
      const query = { conditions: JSON.stringify(conditions) };
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.rules.preview.$get({
            param: scope.param,
            query,
          })
        : await client.merchant[":merchantId"].samples.rules.preview.$get({
            param: scope.param,
            query,
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId && conditions !== null,
  });
};

// ── Exemptions ("individually exempt" creators, exempt from all rules) ─────────

export const useSampleExemptions = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: sampleExemptionsKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.rules.exemptions.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].samples.rules.exemptions.$get({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useAddSampleExemptions = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorOpenId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.rules.exemptions.$post({
            param: scope.param,
            json: { creatorOpenId },
          })
        : await client.merchant[":merchantId"].samples.rules.exemptions.$post({
            param: scope.param,
            json: { creatorOpenId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sample-rule-exemptions"] });
    },
  });
};

export const useRemoveSampleExemption = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorOpenId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].samples.rules.exemptions[
            ":creatorOpenId"
          ].$delete({
            param: { ...scope.param, creatorOpenId },
          })
        : await client.merchant[":merchantId"].samples.rules.exemptions[":creatorOpenId"].$delete({
            param: { ...scope.param, creatorOpenId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sample-rule-exemptions"] });
    },
  });
};
